import React, { useState } from 'react';
import { Edit as EditIcon, RemoveCircle as RemoveIcon } from '@mui/icons-material';
import { Button } from '@mui/material';
import ConfirmDialog from '@shared/components/confirmDialog/ConfirmDialog';
import cx from 'classnames';
import { COMMENT_TYPES } from './constants/commentType';
import { COMMENT_ACTION_TYPES } from './constants/commentActionType';

const CONFIRMATION_DELETE_MESSAGE = 'Are you sure you want to delete this comment?';
const SYSTEM_GENERATED_SENDER_NAME = 'System Generated';

/**
 * @typedef {object} CommentCardProps
 * @property {Object} [comment]
 * @property {number} [selectedCommentId]
 * @property {number} [focusedCommentId]
 * @property {Object} [focusedCommentRef]
 * @property {(id: number) => void} editComment
 * @property {(id: number) => void | Promise<void>} deleteComment
 */

/**
 * @param {CommentCardProps} props
 */
const CommentCard = props => {
  const {
    comment = {},
    selectedCommentId = 0,
    focusedCommentId = 0,
    focusedCommentRef,
    editComment,
    deleteComment
  } = props;

  const [openModal, setOpenModal] = useState(false);

  const handleOnDeleteButtonClick = () => {
    setOpenModal(true);
  };

  const handleClose = () => {
    setOpenModal(false);
  };

  const handleEdit = () => {
    editComment(comment.id);
  };

  const handleDelete = async () => {
    await deleteComment(comment.id);
    setOpenModal(false);
  };

  return (
    <>
      <div
        ref={focusedCommentId === comment.id ? focusedCommentRef : null}
        className={cx('card', {
          'outline-orange': comment.type === COMMENT_TYPES.SYSTEMIC_COMMENT,
          selected: focusedCommentId === comment.id || selectedCommentId === comment.id
        })}>
        <div className='comment-card'>
          <div className='card-header'>
            <span className='bold'>
              {comment.isSystemSender ? SYSTEM_GENERATED_SENDER_NAME : comment.externalUserName}
            </span>
            <span>
              {comment.modifiedDate} at {comment.modifiedTime}
            </span>
            <span>To: {comment.commentTo}</span>
            {comment.actionType === COMMENT_ACTION_TYPES.EDITED && <em>Edited</em>}
          </div>
          <div className='card-body'>{comment.comment}</div>
        </div>

        {selectedCommentId !== comment.id && (
          <div className='action-buttons'>
            {comment.isEditable && (
              <Button onClick={handleEdit}>
                <EditIcon fontSize='large' />
              </Button>
            )}
            {comment.isDeletable && (
              <Button onClick={handleOnDeleteButtonClick}>
                <RemoveIcon fontSize='large' />
              </Button>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={openModal}
        handleClose={handleClose}
        handleOnConfirm={handleDelete}
        message={CONFIRMATION_DELETE_MESSAGE}
        confirmBtnText='Delete'
        cancelBtnText='Cancel'
      />
    </>
  );
};

export default CommentCard;
